import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Upload, Building2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MediaKitLogosTab() {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    logo_url: "",
    website_url: "",
    order: 0,
    active: true
  });

  useEffect(() => {
    fetchLogos();
  }, []);

  const fetchLogos = async () => {
    try {
      const res = await axios.get(`${API}/media-kit/client-logos/all`);
      setLogos(res.data.logos || []);
    } catch (error) {
      toast.error("Error al cargar logos");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error("La imagen no debe superar 2MB");
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.post(`${API}/upload`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setFormData({ ...formData, logo_url: res.data.url });
      toast.success("Logo subido");
    } catch (error) {
      toast.error("Error al subir logo");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("admin_token");

    try {
      if (editingLogo) {
        await axios.put(
          `${API}/media-kit/client-logos/${editingLogo.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Logo actualizado");
      } else {
        await axios.post(
          `${API}/media-kit/client-logos`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Logo creado");
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchLogos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este logo?")) return;
    
    const token = localStorage.getItem("admin_token");
    try {
      await axios.delete(`${API}/media-kit/client-logos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Logo eliminado");
      fetchLogos();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const openEdit = (logo) => {
    setEditingLogo(logo);
    setFormData({
      name: logo.name,
      logo_url: logo.logo_url || "",
      website_url: logo.website_url || "",
      order: logo.order || 0,
      active: logo.active !== false
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingLogo(null);
    setFormData({
      name: "",
      logo_url: "",
      website_url: "",
      order: 0,
      active: true
    });
  };

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Logos de Clientes</h2>
          <p className="text-sm text-gray-500">
            Logos de marcas que aparecerán en "Nuestros Clientes"
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          className="bg-[#FFE500] hover:bg-[#FFE500]/90 text-black"
          data-testid="add-logo-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar Logo
        </Button>
      </div>

      {/* Logos Grid */}
      {logos.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No hay logos de clientes</p>
          <p className="text-sm text-gray-400">Agrega logos de las marcas con las que has colaborado</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {logos.map((logo, index) => (
            <div
              key={logo.id}
              className={`relative bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${
                !logo.active ? "opacity-50" : ""
              }`}
              data-testid={`logo-item-${index}`}
            >
              {/* Logo Image */}
              <div className="aspect-square flex items-center justify-center mb-2">
                {logo.logo_url ? (
                  <img
                    src={logo.logo_url}
                    alt={logo.name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <Building2 className="w-12 h-12 text-gray-400" />
                )}
              </div>

              {/* Name */}
              <p className="text-xs text-center text-gray-600 truncate">{logo.name}</p>

              {/* Actions overlay */}
              <div className="absolute top-1 right-1 flex gap-1">
                <Button
                  variant="secondary"
                  size="icon"
                  className="w-6 h-6 bg-white/90 hover:bg-white"
                  onClick={() => openEdit(logo)}
                  data-testid={`edit-logo-${index}`}
                >
                  <Edit2 className="w-3 h-3" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="w-6 h-6 bg-white/90 hover:bg-red-50 text-red-500"
                  onClick={() => handleDelete(logo.id)}
                  data-testid={`delete-logo-${index}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingLogo ? "Editar Logo" : "Agregar Logo de Cliente"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre de la Marca *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Prudence, Activision..."
                required
                data-testid="input-name"
              />
            </div>

            <div>
              <Label>Logo de la Marca</Label>
              <div className="mt-2">
                {formData.logo_url ? (
                  <div className="relative bg-gray-100 rounded-lg p-4 flex items-center justify-center">
                    <img
                      src={formData.logo_url}
                      alt="Preview"
                      className="max-h-24 object-contain"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData({ ...formData, logo_url: "" })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      data-testid="input-logo-upload"
                    />
                    {uploading ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E91E8C]" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">Click para subir logo</p>
                        <p className="text-xs text-gray-400">PNG, SVG o JPG hasta 2MB</p>
                      </>
                    )}
                  </label>
                )}
              </div>
              
              {/* Or paste URL */}
              <div className="mt-2">
                <Input
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="O pega una URL del logo..."
                  data-testid="input-logo-url"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="website_url">Sitio Web (opcional)</Label>
              <Input
                id="website_url"
                value={formData.website_url}
                onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                placeholder="https://..."
                data-testid="input-website"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="order">Orden</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  data-testid="input-order"
                />
              </div>

              <div className="flex items-center gap-3 pt-6">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  data-testid="switch-active"
                />
                <Label htmlFor="active">Activo</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#FFE500] hover:bg-[#FFE500]/90 text-black"
                data-testid="save-logo-btn"
              >
                {editingLogo ? "Guardar Cambios" : "Agregar Logo"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MediaKitLogosTab;
